import React, { useState } from 'react';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { Icon, SelectMenu, Typography } from '@scale/scaleui';
import { NumericThresholdTypeSchemaMap } from '@scale/shared/pipelinev3/genai';
import {
  NumericThreshold,
  NumericRoutingTypeOverrideType,
  NumericValueComparator,
} from '@scale/shared-types';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface NumericRoutingThresholdsBuilderProps {
  name: string;
  existingValue?: Array<NumericThreshold>;
  updateProperty: (arg: Array<NumericThreshold> | undefined) => void;
  required?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}

const stepTypeOptions = [
  {
    id: NumericRoutingTypeOverrideType.ModelResponseSelector,
    name: 'Model Response Selector Step Type',
  },
  {
    id: NumericRoutingTypeOverrideType.TextCollection,
    name: 'Text Collection Step Type',
  },
  {
    id: NumericRoutingTypeOverrideType.TextCollectionPerResponse,
    name: 'Text Collection Per Response Step Type',
  },
  {
    id: NumericRoutingTypeOverrideType.QualityMeasurement,
    name: 'Quality Measurement Step Type',
  },
];

const NumericRoutingThresholdsBuilder: React.FC<NumericRoutingThresholdsBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const thresholds = existingValue;
  const [selectedStepType, setSelectedStepType] = useState<NumericRoutingTypeOverrideType>(
    NumericRoutingTypeOverrideType.TextCollection,
  );

  const addThreshold = (stepType: NumericRoutingTypeOverrideType) => {
    const newThresholds = [...thresholds];
    newThresholds.push({
      stepType,
      value: 0,
      comparator: NumericValueComparator.Equals,
      ...(stepType === NumericRoutingTypeOverrideType.ModelResponseSelector && {
        checkIsObjective: false,
        isObjective: false,
        useFloat: false,
      }),
      ...(stepType === NumericRoutingTypeOverrideType.TextCollectionPerResponse && {
        modelId: '',
      }),
      ...((stepType === NumericRoutingTypeOverrideType.TextCollection ||
        stepType === NumericRoutingTypeOverrideType.TextCollectionPerResponse) && { fieldId: '' }),
    } as any);

    updateProperty(newThresholds);
  };

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-sm font-medium leading-6 text-neutral-700">{name}</div>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={thresholds}
        setElements={updateProperty}
        getKey={threshold => JSON.stringify(threshold)}
        getTitle={threshold => `${threshold.stepType}`}
        getSchema={threshold => NumericThresholdTypeSchemaMap[threshold.stepType]}
        delimiter={<Typography.Subtitle4>AND</Typography.Subtitle4>}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={stepTypeOptions}
          selectedOptionId={selectedStepType}
          setSelected={value => {
            setSelectedStepType(value as NumericRoutingTypeOverrideType);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addThreshold(selectedStepType)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default NumericRoutingThresholdsBuilder;
